import React from "react";
import { Tool, useDeleteTool } from "@/lib/tools-store";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Edit3, Copy, Trash2, Play, Activity } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
interface ToolTableProps {
  tools: Tool[];
  onEdit: (tool: Tool) => void;
}
export function ToolTable({ tools, onEdit }: ToolTableProps) {
  const deleteTool = useDeleteTool();
  return (
    <div className="rounded-xl border border-white/5 bg-card/50 overflow-hidden">
      <Table>
        <TableHeader className="bg-muted/30">
          <TableRow>
            <TableHead className="w-[30%]">Tool</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Usage</TableHead>
            <TableHead>Health</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tools.map((tool) => (
            <TableRow key={tool.id} className="hover:bg-muted/20">
              <TableCell>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center border border-white/10 shrink-0">
                    <img src={tool.iconUrl} alt={tool.name} className="w-4 h-4 object-contain" />
                  </div>
                  <div>
                    <div className="font-bold text-sm">{tool.name}</div>
                    <div className="text-[10px] text-muted-foreground uppercase">{tool.provider}</div>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="secondary" className="text-[10px] font-medium">{tool.category}</Badge>
              </TableCell>
              <TableCell>
                <span className="text-xs font-mono">{tool.type}</span>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5">
                   <Activity className="w-3 h-3 text-chart-3" />
                   <span className="text-xs font-bold">{tool.metrics.calls.toLocaleString()}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  <div className="flex items-center justify-between gap-2 text-[10px]">
                    <span className="text-muted-foreground">Success</span>
                    <span className={tool.metrics.successRate > 98 ? 'text-primary font-bold' : 'text-amber-500 font-bold'}>{tool.metrics.successRate}%</span>
                  </div>
                  <div className="w-16 h-1 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${tool.metrics.successRate}%` }} />
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={`capitalize text-[10px] ${tool.status === 'connected' ? 'text-primary border-primary/20' : 'text-destructive border-destructive/20'}`}>
                  {tool.status}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Play className="w-4 h-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(tool)} className="gap-2"><Edit3 className="w-4 h-4" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem className="gap-2"><Copy className="w-4 h-4" /> Clone</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => deleteTool(tool.id)} className="text-destructive gap-2"><Trash2 className="w-4 h-4" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
